import { useCallback, useMemo, useState } from "react";
import type { FormEvent } from "react";
import { useForm } from "@mantine/form";
import { useQueryClient } from "@tanstack/react-query";
import {
  legacyReportsService,
  LegacyReportRequestError,
} from "../services/legacyReports.service";
import type {
  LegacyReportDraftData,
  LegacyReportCropInput,
  LegacyReportReviewState,
} from "../types/legacyReportReview";
import {
  canRetainLegacyReportEdits,
  createLegacyReportDraftValues,
  legacyReportApprovalBlockers,
  serializeLegacyReportDraft,
  type LegacyRenderCompletion,
} from "../utils/legacyReportReview";

export function useLegacyReportEditor(
  initial: LegacyReportReviewState & {
    draft: NonNullable<LegacyReportReviewState["draft"]>;
  }
) {
  const client = useQueryClient();
  const [saved, setSaved] = useState<LegacyReportReviewState>(initial);
  const [latest, setLatest] = useState<LegacyReportReviewState | null>(null);
  const [pending, setPending] = useState<string | null>(null);
  const [error, setError] = useState<LegacyReportRequestError | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [rendered, setRendered] = useState<LegacyRenderCompletion | null>(null);
  const [renderFailed, setRenderFailed] = useState(false);
  const [renderAttempt, setRenderAttempt] = useState(0);
  const form = useForm<LegacyReportDraftData>({
    initialValues: createLegacyReportDraftValues(initial.draft),
    validateInputOnBlur: true,
    validate: {
      header: {
        caseNumber: (value: string) =>
          value.trim() ? null : "Keep the printed case number.",
      },
      sections: {
        text: (value: string) =>
          value.length <= 100000
            ? null
            : "This section exceeds the allowed length.",
      },
      warningResolutions: {
        note: (value: string) =>
          !value.trim() || (value.trim().length >= 10 && value.length <= 3000)
            ? null
            : "Describe the source check in 10–3000 characters, or leave unresolved.",
      },
    },
  });
  const draft = saved.draft!;
  const dirty = form.isDirty();
  const blockers = useMemo(
    () => legacyReportApprovalBlockers(saved, dirty, rendered),
    [saved, dirty, rendered]
  );
  const mayRetainEdits = useMemo(
    () => Boolean(latest && canRetainLegacyReportEdits(saved, latest)),
    [saved, latest]
  );
  const publish = useCallback(
    (next: LegacyReportReviewState) => {
      setSaved(next);
      client.setQueryData(["legacy-report-review", next.caseId], next);
    },
    [client]
  );
  const recordError = useCallback((failure: unknown) => {
    setError(
      failure instanceof LegacyReportRequestError
        ? failure
        : new LegacyReportRequestError()
    );
  }, []);
  const handleRendered = useCallback(
    ({ pageCount }: { pageCount: number }) => {
      setRenderFailed(false);
      setRendered(
        pageCount > 0
          ? { pageCount, fingerprint: draft.contentFingerprint }
          : null
      );
    },
    [draft.contentFingerprint]
  );
  const handleRenderPending = useCallback(() => {
    setRendered(null);
    setRenderFailed(false);
  }, []);
  const handleRenderError = useCallback(() => {
    setRendered(null);
    setRenderFailed(true);
    setMessage(null);
  }, []);
  const handleRetryRender = useCallback(() => {
    setRendered(null);
    setRenderFailed(false);
    setRenderAttempt((attempt) => attempt + 1);
  }, []);
  const handleSave = useCallback(
    async (event?: FormEvent<HTMLFormElement>) => {
      event?.preventDefault();
      if (pending || saved.baseline || !saved.capabilities.canPrepare) return;
      const validation = form.validate();
      if (validation.hasErrors) {
        const field = Object.keys(validation.errors)[0];
        const node = form.getInputNode(field);
        node?.scrollIntoView?.({ block: "center" });
        node?.focus();
        return;
      }
      setPending("save");
      setError(null);
      setMessage(null);
      try {
        const result = await legacyReportsService.save(
          saved.caseId,
          draft.revision,
          serializeLegacyReportDraft(form.getValues())
        );
        publish(result);
        if (result.draft) {
          const values = createLegacyReportDraftValues(result.draft);
          form.setValues(values);
          form.resetDirty(values);
        }
        setRendered(null);
        setMessage(
          `Saved revision ${result.draft?.revision}. Render and compare this revision before approval.`
        );
      } catch (failure) {
        recordError(failure);
      } finally {
        setPending(null);
      }
    },
    [pending, saved, form, draft.revision, publish, recordError]
  );
  const runCandidateAction = useCallback(
    async (action: "preview" | "compare") => {
      if (pending) return;
      if (form.isDirty()) {
        setError(
          new LegacyReportRequestError(400, "reconstruction_review_required")
        );
        return;
      }
      setPending(action);
      setError(null);
      setMessage(null);
      try {
        const result = await legacyReportsService[action](
          saved.caseId,
          draft.revision
        );
        publish(result);
        setMessage(
          action === "compare"
            ? "Comparison completed for the saved revision. Review the findings below."
            : "The saved revision was rendered. Review every page and its source evidence."
        );
      } catch (failure) {
        recordError(failure);
      } finally {
        setPending(null);
      }
    },
    [pending, form, saved.caseId, draft.revision, publish, recordError]
  );
  const handlePreview = useCallback(() => {
    void runCandidateAction("preview");
  }, [runCandidateAction]);
  const handleCompare = useCallback(() => {
    void runCandidateAction("compare");
  }, [runCandidateAction]);
  const handleApprove = useCallback(
    async (acknowledgedMinorIds: string[]) => {
      if (
        pending ||
        blockers.length ||
        !saved.latestPreview ||
        !saved.latestComparison
      )
        return;
      setPending("approve");
      setError(null);
      setMessage(null);
      try {
        const acknowledgement = await legacyReportsService.acknowledge(
          saved.caseId,
          {
            expectedRevision: draft.revision,
            previewId: saved.latestPreview.id,
            comparisonId: saved.latestComparison.id,
            sourceReviewed: true,
            acknowledgedMinorIds,
          }
        );
        const result = await legacyReportsService.approve(
          saved.caseId,
          draft.revision,
          acknowledgement.acknowledgementId
        );
        publish(result);
        setMessage(
          "Historical reconstruction approved. This baseline is now immutable."
        );
        void client.invalidateQueries({
          queryKey: ["legacy-import", saved.caseId],
        });
        void client.invalidateQueries({ queryKey: ["case", saved.caseId] });
      } catch (failure) {
        recordError(failure);
      } finally {
        setPending(null);
      }
    },
    [pending, blockers, saved, draft.revision, publish, recordError, client]
  );
  const handleGetLatest = useCallback(async () => {
    if (pending) return;
    setPending("refresh");
    try {
      setLatest(await legacyReportsService.get(saved.caseId));
    } catch (failure) {
      recordError(failure);
    } finally {
      setPending(null);
    }
  }, [pending, saved.caseId, recordError]);
  const handleCrop = useCallback(
    async (input: LegacyReportCropInput) => {
      if (pending || form.isDirty() || saved.baseline) return false;
      setPending("crop");
      setError(null);
      try {
        const result = await legacyReportsService.crop(
          saved.caseId,
          draft.revision,
          input
        );
        publish(result);
        if (result.draft) {
          const values = createLegacyReportDraftValues(result.draft);
          form.setValues(values);
          form.resetDirty(values);
        }
        setRendered(null);
        setMessage(
          "Source crop saved. Review its complete graphic and boundaries, then compare the updated report."
        );
        return true;
      } catch (failure) {
        recordError(failure);
        return false;
      } finally {
        setPending(null);
      }
    },
    [pending, form, saved, draft.revision, publish, recordError]
  );
  const handleCloseConflict = useCallback(() => setLatest(null), []);
  const handleLoadLatest = useCallback(() => {
    if (!latest?.draft) return;
    publish(latest);
    const values = createLegacyReportDraftValues(latest.draft);
    form.setValues(values);
    form.resetDirty(values);
    setLatest(null);
    setError(null);
    setRendered(null);
  }, [latest, form, publish]);
  const handleKeepEdits = useCallback(() => {
    if (!latest?.draft || !mayRetainEdits) return;
    publish(latest);
    form.resetDirty(createLegacyReportDraftValues(latest.draft));
    setLatest(null);
    setError(null);
    setRendered(null);
    setMessage(
      "Your edits are retained against the latest revision. Save them before rendering again."
    );
  }, [latest, mayRetainEdits, publish, form]);
  return {
    saved,
    draft,
    form,
    dirty,
    pending,
    error,
    message,
    rendered,
    renderFailed,
    renderAttempt,
    blockers,
    latest,
    mayRetainEdits,
    handleSave,
    handlePreview,
    handleCompare,
    handleApprove,
    handleRendered,
    handleRenderPending,
    handleRenderError,
    handleRetryRender,
    handleGetLatest,
    handleCrop,
    handleCloseConflict,
    handleLoadLatest,
    handleKeepEdits,
  };
}
